import {
  Component,
  computed,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Injector,
  Input,
  model,
  OnInit,
  Output,
  runInInjectionContext,
  untracked,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslateModule } from '@ngx-translate/core';

import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import { UserStore } from '@signal-store/index';
import { DocumentDetailsStore } from '../common';
import { AccessLogTabComponent } from './access-log-tab/access-log-tab.component';
import { ActivityTabComponent } from './activity-tab/activity-tab.component';
import { PdfMarkupTabComponent } from './pdf-markup-tab/pdf-markup-tab.component';
import { ProgressPaymentsTabComponent } from './progress-payments-tab/progress-payments-tab.component';
import { RelationsTabComponent } from './relations-tab/relations-tab.component';
import { SignaturesTabComponent } from './signatures-tab/signatures-tab.component';

/** The container of this component should provide DocumentDetailsStore, and set the params:
 * @ documentDetailsStore.setParams({contractId, moduleId, entityInstanceId})
 *
 * This allows to create a separate store for each instance of the component, keeping its state isolated.
 *
 * After setting the params, load the data needed (activity, relations, etc).
 *
 * You don't need to pass any parameters to methods, as the store gets them from params.
 *  */
@Component({
  selector: 'cipo-document-details-tabs',
  templateUrl: './document-details-tabs.component.html',
  imports: [
    MatTabsModule,
    /* Avoids circular dependencies */
    forwardRef(() => RelationsTabComponent),
    AccessLogTabComponent,
    PdfMarkupTabComponent,
    ProgressPaymentsTabComponent,
    MatBadgeModule,
    TranslateModule,
    ActivityTabComponent,
    SignaturesTabComponent,
  ],
})
export class DocumentDetailsTabsComponent implements OnInit {
  private dmStore = inject(DocumentDetailsStore);
  /* make required input after contracts implementation */
  entityInstanceId = model<number>();
  currentDocument = computed(() => this.dmStore.documents()[this.entityInstanceId()]);

  /* This is required for contract details */
  @Input() set entityinstanceid(value: number) {
    this.entityInstanceId.set(value);
  }
  @Input() fromcontractdetails = false;
  @Output() rollbacktriggered = new EventEmitter<void>();
  private userStore = inject(UserStore);
  private injector = inject(Injector);

  ngOnInit() {
    if (this.fromcontractdetails) {
      this.dmStore.setDocumentParams({
        contractId: 0,
        entityInstanceId: this.entityInstanceId(),
        moduleId: SystemModulesEnum.Contracts,
        screenId: null,
        workflowId:
          this.userStore.menuItemsList().find(m => m.moduleId === SystemModulesEnum.Contracts)?.workflowId ?? null,
      });
      runInInjectionContext(this.injector, () => {
        this.dmStore.loadGeneralInformation({
          contractId: 0,
          entityInstanceId: this.entityInstanceId(),
          moduleId: SystemModulesEnum.Contracts,
        });
        effect(() => {
          const rollbackSuccess = this.currentDocument()?.loadingRollback === 'success';
          if (rollbackSuccess) {
            untracked(() => {
              /* Clear before emit so AngularJS remount does not re-trigger this effect */
              this.dmStore.clearLoadingRollback(this.entityInstanceId());
              this.rollbacktriggered.emit();
            });
          }
        });
      });
    }
  }
}
